import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import texts from '../locales/texts';
import theme, { mobileBreakpoint } from '../theme';
import { useAuth } from '../AuthContext';

const FooterContainer = styled.footer`
  background-color: white;
  border-radius: 16px;
  padding: 32px 24px;
  margin-top: 60px;
`;

const FooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 30px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const FooterSection = styled.div`
  flex: 1;
  min-width: 200px;
`;

const FooterTitle = styled.h3`
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterListItem = styled.li`
  margin-bottom: 8px;
`;

const FooterLink = styled(Link)`
  text-decoration: none;
  color: #666;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const FooterBottom = styled.div`
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const FooterCopyright = styled.div`
  color: #888;
  font-size: 14px;
`;

const FooterLegal = styled.div`
  display: flex;
  gap: 16px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FooterLegalLink = styled(Link)`
  color: #888;
  font-size: 14px;
  text-decoration: none;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
    text-decoration: underline;
  }
`;

const FooterClickableText = styled.span`
  color: #666;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const Footer = ({ onTerminClick }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  const handleProgramLinkClick = (e, hash) => {
    // If we're already on the program page, manually scroll to the anchor
    if (location.pathname === '/program') {
      e.preventDefault();
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // Otherwise, let the Link component handle navigation normally
  };
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <FooterTitle>{texts.footer_0_0}</FooterTitle>
          <FooterList>
            <FooterListItem>
              <FooterLink 
                to="/about" 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                {texts.footer_0_1}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink 
                to="/program" 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                {texts.footer_0_2}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              {!isAuthenticated() && (
                <FooterClickableText onClick={onTerminClick}>
                  {texts.footer_0_3}
                </FooterClickableText>
              )}
            </FooterListItem>
          </FooterList>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>{texts.footer_1_0}</FooterTitle>
          <FooterList>
            <FooterListItem>
              <FooterLink 
                to="/program#nachhilfe"
                onClick={(e) => handleProgramLinkClick(e, '#nachhilfe')}
              >
                {texts.footer_1_1}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink 
                to="/program#sprachunterricht"
                onClick={(e) => handleProgramLinkClick(e, '#sprachunterricht')}
              >
                {texts.footer_1_3}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink 
                to="/program#gruppenkurse"
                onClick={(e) => handleProgramLinkClick(e, '#gruppenkurse')}
              >
                {texts.footer_1_2}
              </FooterLink>
            </FooterListItem>
          </FooterList>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>{texts.footer_2_0}</FooterTitle>
          <FooterList>
            <FooterListItem>
              Email:{' '}
            <a href="mailto:mey@meyteaches.de" style={{ color: '#666', textDecoration: 'none' }}>
                {texts.footer_2_1}
              </a>
            </FooterListItem>
          </FooterList>
        </FooterSection>
      </FooterContent>
      
      <FooterBottom>
        <FooterCopyright>
          © {new Date().getFullYear()} {texts.companyFull}. {texts.copyright}
        </FooterCopyright>
        <FooterLegal>
          <FooterLegalLink to="/terms#privacy">{texts.privacyPolicy}</FooterLegalLink>
          <FooterLegalLink to="/terms#agb">{texts.termsOfService}</FooterLegalLink>
          <FooterLegalLink to="/terms#impressum">{texts.impressum}</FooterLegalLink>
          <FooterLegalLink to="/about-project">{texts.footer_3_1}</FooterLegalLink>
        </FooterLegal>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer; 